import { useEffect, useRef, useState } from 'react';

const DEFAULT_INTERVAL = 500;

export const useThrottledValue = <T>(value: T, interval: number = DEFAULT_INTERVAL) => {
  const [throttledValue, setThrottledValue] = useState<T>(value);
  const lastExecutedRef = useRef<number>(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const now = Date.now();
    const timeSinceLast = now - lastExecutedRef.current;

    if (timeSinceLast >= interval) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }

      lastExecutedRef.current = now;
      setThrottledValue(value);

      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = undefined;
        }
      };
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      lastExecutedRef.current = Date.now();
      setThrottledValue(value);
      timeoutRef.current = undefined;
    }, Math.max(interval - timeSinceLast, 0));

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }
    };
  }, [value, interval]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return throttledValue;
};

export default useThrottledValue;
